/**
 * Projekte-Repository.
 *
 * Ein Projekt buendelt Aufgaben und zahlt optional auf ein Ziel ein - es
 * schliesst damit die Luecke zwischen "was mache ich heute" und "wo will ich
 * hin".
 */
import { db } from '@/db/schema';
import { DEFAULT_PROJECT_PRIORITY } from '@/domain/priority';
import { nowInstant } from '@/lib/datetime';
import { newId } from '@/lib/utils/id';
import { applyLinkRule, setTaskProject } from '@/db/repositories/links';
import { inDenPapierkorb } from '@/db/repositories/papierkorb';
import type { ID, Project, ProjectStatus, Task } from '@/domain/types';

export const PROJECT_STATUSES: ProjectStatus[] = [
  'aktiv', 'pausiert', 'irgendwann', 'abgeschlossen',
];

export type ProjectDraft = Pick<Project, 'title'>
  & Partial<Pick<Project, 'description' | 'status' | 'areaId' | 'goalId' | 'deadline' | 'deadlineTime' | 'priority'>>;

export async function listProjects(): Promise<Project[]> {
  const alle = await db.projects.toArray();
  return alle.sort(
    (a, b) =>
      PROJECT_STATUSES.indexOf(a.status) - PROJECT_STATUSES.indexOf(b.status)
      || a.title.localeCompare(b.title, 'de'),
  );
}

export async function createProject(draft: ProjectDraft): Promise<Project> {
  const ts = nowInstant();
  const project: Project = {
    id: newId(),
    areaId: draft.areaId ?? null,
    title: draft.title.trim(),
    description: draft.description?.trim() || undefined,
    status: draft.status ?? 'aktiv',
    priority: draft.priority ?? DEFAULT_PROJECT_PRIORITY,
    goalId: draft.goalId,
    // Nur gesetzte Felder speichern; eine Uhrzeit ohne Datum ist keine Deadline.
    ...(draft.deadline ? { deadline: draft.deadline } : {}),
    ...(draft.deadline && draft.deadlineTime ? { deadlineTime: draft.deadlineTime } : {}),
    // Spalten sind fuer ein spaeteres Kanban-Brett vorgesehen und bleiben
    // vorerst leer - das Feld existiert, damit es dafuer keine Migration braucht.
    columns: [],
    createdAt: ts,
    updatedAt: ts,
  };
  // Mit Ziel gilt dessen Bereich, nicht der gewaehlte (Roadmap F-11).
  await db.transaction('rw', db.areas, db.goals, db.projects, db.tasks, async () => {
    await db.projects.add(project);
    await applyLinkRule();
  });
  return (await db.projects.get(project.id)) ?? project;
}

export async function updateProject(
  id: ID,
  patch: Partial<Omit<Project, 'id' | 'createdAt'>>,
): Promise<void> {
  // Ziel oder Bereich geaendert: Bereich des Projekts und seiner Aufgaben
  // nach der Regel neu bestimmen.
  const verknuepfung = 'goalId' in patch || 'areaId' in patch;
  await db.transaction('rw', db.areas, db.goals, db.projects, db.tasks, async () => {
    await db.projects.where('id').equals(id).modify((p) => {
      Object.assign(p, patch, { updatedAt: nowInstant() });
      // Ein entferntes Feld (Ziel, Deadline, ...) kommt als `undefined` - der
      // Schluessel wird dann ganz entfernt statt leer gespeichert.
      for (const key of Object.keys(patch) as (keyof typeof patch)[]) {
        if (patch[key] === undefined) delete p[key];
      }
    });
    if (verknuepfung) await applyLinkRule();
  });
}

export async function setProjectStatus(id: ID, status: ProjectStatus): Promise<void> {
  await updateProject(id, { status });
}

/**
 * Loescht das Projekt und haengt seine Aufgaben ab.
 *
 * Aufgaben werden ausdruecklich NICHT mitgeloescht: sie sind eigenstaendige
 * Arbeit, und ein stillschweigend mitgeloeschter Stapel waere Datenverlust
 * ohne Rueckfrage. Sie bleiben mit ihrem Status erhalten, nur ohne Projekt.
 */
export async function deleteProject(id: ID): Promise<void> {
  // Papierkorb (F-45): beim Wiederherstellen haengt alles wieder am Projekt.
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'projects', zeileIds: [id] }, () => db.transaction('rw', [db.projects, db.tasks, db.events, db.books, db.resources, db.thoughts], async () => {
    const ts = nowInstant();
    // Notizen und Gedanken (F-17) bleiben, verlieren nur den Verweis.
    await db.resources.where('projectId').equals(id).modify((r) => { delete r.projectId; r.updatedAt = ts; });
    await db.thoughts.where('projectId').equals(id).modify((g) => { delete g.projectId; g.updatedAt = ts; });
    await db.tasks.where('projectId').equals(id).modify((task) => {
      delete task.projectId;
      task.updatedAt = ts;
    });
    // Verknuepftes Wissen (F-19) bleibt, verliert nur dieses Projekt.
    await db.books.where('projectIds').equals(id).modify((b) => {
      b.projectIds = (b.projectIds ?? []).filter((x) => x !== id);
      b.updatedAt = ts;
    });
    // Verknuepfte Termine (F-04 d) bleiben, verlieren nur den Verweis.
    await db.events.where('projectId').equals(id).modify((event) => {
      delete event.projectId;
      event.updatedAt = ts;
    });
    await db.projects.delete(id);
  }));
}

export async function tasksOfProject(projectId: ID): Promise<Task[]> {
  const rows = await db.tasks.where('projectId').equals(projectId).toArray();
  return rows.sort((a, b) => a.order - b.order);
}

/**
 * Eine bestehende Aufgabe einem Projekt zuordnen - `null` loest sie wieder.
 *
 * Weiterleitung auf `setTaskProject`, damit auch dieser Weg die
 * Verknuepfungsregel einhaelt (Bereich folgt dem Projekt, kein Zielverweis
 * daneben).
 */
export async function assignToProject(taskId: ID, projectId: ID | null): Promise<void> {
  await setTaskProject(taskId, projectId);
}
